import React, { useState, useEffect, useCallback, useReducer } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { useDropzone } from 'react-dropzone'
import { BlobServiceClient } from '@azure/storage-blob'
import CloseIcon from '@mui/icons-material/Close'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import MenuItem from '@mui/material/MenuItem'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormLabel from '@mui/material/FormLabel'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import JSZip from 'jszip'
import { uploadFileData } from '../../actions/userActions'
import { closePopup } from '../../actions/uiActions'
import { getDatabaseJobFiles, saveDatabaseJobFile, deleteDatabaseJobFile, purgePreviosJobFiles, dataToolsDeleteBlobsFromUnfinishedJob, getProjects } from '../../actions/dbFunctionsActions'

function DatabaseJobFileUpload(props) {
    const BLOB_SIZE = 100 * 1024
    const BLOB_BLOCK_SIZE = 4 * 1024 * 1024
    const CONCURRENCY = 3
    const onDrop = useCallback(async acceptedFiles => {
        setIsLoadingRequest(true)
        let files = []
        let allFileNames = []
        let processedFiles = []
        let totalFilesProcessed = 0
        let rejectedFiles = []
        let fileSnips = []

        const jobLookupId = (props.popupInfo.jobData) ? props.popupInfo.jobData.id : -1
        const inBrandNewFileMode = jobLookupId === -1

        if (acceptedFiles.length > 0) {
            if (inBrandNewFileMode) {
                for (let fileItr = 0; fileItr < acceptedFiles.length; fileItr++) {
                    if (isThisFileZipped(acceptedFiles[fileItr]["name"])) {
                        files.push(acceptedFiles[fileItr])
                        allFileNames.push(acceptedFiles[fileItr]["name"].toLowerCase().trim())
                    } else if (isThisFileTypeAccepted(acceptedFiles[fileItr]["name"])) {
                        files.push(acceptedFiles[fileItr])
                        allFileNames.push(acceptedFiles[fileItr]["name"].toLowerCase().trim())
                    } else if (!isThisFileTypeAccepted(acceptedFiles[fileItr]["name"])) {
                        rejectedFiles.push(acceptedFiles[fileItr]["name"])
                    }
                }
            } else {
                if (isThisFileZipped(acceptedFiles[0]["name"])) {
                    files.push(acceptedFiles[0])
                    allFileNames.push(acceptedFiles[0]["name"].toLowerCase().trim())
                } else if (isThisFileTypeAccepted(acceptedFiles[0]["name"])) {
                    files.push(acceptedFiles[0])
                    allFileNames.push(acceptedFiles[0]["name"].toLowerCase().trim())
                } else if (!isThisFileTypeAccepted(acceptedFiles[0]["name"])) {
                    rejectedFiles.push(acceptedFiles[0]["name"])
                }
            }
        }
        console.log(projectIdRef.current)
        let lookupExistingFiles = props.popupInfo.existingFiles.filter(f => f.projectId == projectIdRef.current && allFileNames.includes(f.fileName.toLowerCase().trim()) && f.id !== jobLookupId)
        let lookupWithinZips = doesThisFileNameExistsWithin(props.popupInfo.existingFiles, allFileNames)
        let lookupAllUploadedZipFiles = await doesThisZipFileContainOneFileThatWasUploadedIndividually(files, props.popupInfo.existingFiles)
        let lookupAllUploadedZipFilesJustUploadedFiles = await doesThisZipFileContainOneFileThatWasUploadedIndividually(files, files)
        if (lookupExistingFiles.length > 0) {
            files = []
            setErrorMessage(`File [${lookupExistingFiles[0]["fileName"]}] already exists`)
            setInvalid(true)
        } else if (lookupWithinZips.length > 0) {
            files = []
            setErrorMessage(`File [${lookupWithinZips[0]}] already exists within another zip file`)
            setInvalid(true)
        } else if (lookupAllUploadedZipFiles.length > 0) {
            files = []
            setErrorMessage(`File [${lookupAllUploadedZipFiles[0]}] from zip file is already a global file`)
            setInvalid(true)
        } else if (lookupAllUploadedZipFilesJustUploadedFiles.length > 0) {
            files = []
            setErrorMessage(`File [${lookupAllUploadedZipFilesJustUploadedFiles[0]}] exists in multiple places within the upload`)
            setInvalid(true)
        } else if (!inBrandNewFileMode && !areExtensionsEqual((props.popupInfo.jobData) ? props.popupInfo.jobData.jobFileData : null, JSON.parse(props.popupInfo.jobData.jobFileData).name, allFileNames[0])) {
            files = []
            setErrorMessage(`Files [${allFileNames[0]}] and [${JSON.parse(props.popupInfo.jobData.jobFileData).name}] do not have the same extension`)
            setInvalid(true)
        } else {
            setErrorMessage('')
            setInvalid(false)

            if (rejectedFiles.length > 0) {
                setErrorMessage(`File(s); ${rejectedFiles.join(", ")} are not allowed`)
            }

            if (files.length > 0) {
                setUploadedNewFiles(true)
            }
        }

        if (files.length > 0) {
            let allFileSizesProcessed = 0
            let combinedFileSizes = 0
            for (let fileItr = 0; fileItr < files.length; fileItr++) {
                combinedFileSizes += files[fileItr]["size"]
            }
            setCombinedUploadFileSizes(combinedFileSizes)
            setFileSizeProcessed(allFileSizesProcessed)

            for (let x = 0; x < files.length; x++) {
                let data = {
                    name: files[x]["name"],
                    size: files[x]["size"]
                }

                let dbUploadFileReq = await props.uploadFileData(data)
                let dbUploadFileResp = await dbUploadFileReq.json()

                let isZip = false
                if (isThisFileZipped(files[x]["name"])) {
                    isZip = true
                }

                let fileNames = []
                if (isZip) {
                    let zipCall = await JSZip.loadAsync(files[x])
                    fileNames = Object.keys(zipCall["files"])

                    for (let zipFileItr = 0; zipFileItr < fileNames.length; zipFileItr++) {
                        const currentFile = zipCall.file(fileNames[zipFileItr])
                        try {
                            if (currentFile) {
                                const fileData = await currentFile.async("arraybuffer")
                                const uint8Array = new Uint8Array(fileData)
                                const zipFileSnippet = uint8Array.slice(0, BLOB_SIZE)
                                const textDecoder = new TextDecoder()
                                const fileContent = textDecoder.decode(zipFileSnippet)
                                fileSnips.push({
                                    "fileName": fileNames[zipFileItr],
                                    "rawData": fileContent
                                })
                            }
                            console.log("Finished With File " + fileNames[zipFileItr] + " " + (zipFileItr + 1) + "/" + fileNames.length)
                        } catch {
                            console.log("Error with file " + fileNames[zipFileItr] + " using empty snippet for now, " + (zipFileItr + 1) + "/" + fileNames.length)
                            fileSnips.push({
                                "fileName": fileNames[zipFileItr],
                                "rawData": ''
                            })
                        }
                    }
                }

                processedFiles.push({
                    "name": data.name,
                    "url": dbUploadFileResp["url"],
                    "containerName": dbUploadFileResp["containerName"],
                    "blobName": dbUploadFileResp["blobName"],
                    "identifier": lookupIfWeNeedNewGuid((props.popupInfo.jobData) ? props.popupInfo.jobData.jobFileData : null, data.name, inBrandNewFileMode),
                    "zipIndividualFiles": fileNames
                })

                if (!isZip) {
                    const fileReader = new FileReader()
                    const blob = files[x].slice(0, BLOB_SIZE)
                    fileReader.onload = function (e) {
                        const fileSnippet = e.target.result
                        fileSnips.push({
                            "fileName": data.name,
                            "rawData": fileSnippet
                        })
                    }
                    fileReader.onerror = function (e) { console.error("Error reading file", e) }
                    if (hasValidExtension(data.name)) {
                        fileReader.readAsText(blob)
                    }
                }

                let blobServiceClient = new BlobServiceClient(dbUploadFileResp["url"])
                let containerClient = blobServiceClient.getContainerClient(dbUploadFileResp["containerName"])
                let blockBlobClient = containerClient.getBlockBlobClient(dbUploadFileResp["blobName"])
                let uploadOptions = {
                    blobHTTPHeaders: { blobContentType: files[x]["type"] },
                    blockSize: BLOB_BLOCK_SIZE,
                    concurrency: CONCURRENCY,
                    onProgress: (progress) => {
                        let newUpdateLevel = allFileSizesProcessed + progress["loadedBytes"]
                        setFileSizeProcessed(newUpdateLevel)
                    }
                }

                try {
                    console.log("Uploading File: " + data["name"])
                    await blockBlobClient.uploadData(files[x], uploadOptions)
                    totalFilesProcessed++
                }
                catch (e) {
                    console.error(e)
                }
                finally {
                    allFileSizesProcessed += files[x]["size"]
                    setFileSizeProcessed(allFileSizesProcessed)
                }
            }

            setUploadedFiles(processedFiles)
            setFileSnippets(fileSnips)
        }

        setIsLoadingRequest(false)
    }, [])

    const [isLoadingRequest, setIsLoadingRequest] = useState(false)
    const [invalid, setInvalid] = useState(false)
    const [combinedUploadFileSizes, setCombinedUploadFileSizes] = useState(0)
    const [uploadedFiles, setUploadedFiles] = useState([])
    const [fileSnippets, setFileSnippets] = useState([])
    const [fileSizeProcessed, setFileSizeProcessed] = useState(0)
    const [filesPresaved, setFilesPresaved] = useState([])
    const [errorMessage, setErrorMessage] = useState('')
    const [description, setDescription] = useState('')
    const [editId, setEditId] = useState(-1)
    const [uploadedNewFiles, setUploadedNewFiles] = useState(false)
    const [header, setHeader] = useState('Upload Job Files')
    const [projectId, setProjectId] = useState(-1)
    const [projectList, setProjectList] = useState([])
    const [, forceUpdate] = useReducer(x => x + 1, 0)
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

    const projectIdRef = React.useRef(projectId)

    useEffect(() => {

        projectIdRef.current = projectId

        getProjects().then(json => {
            setProjectList(json.projectList || [])
        }).catch(ex => {
            console.error('Failed to load projects', ex)
            setProjectList([])
        })

        if (props.popupInfo.jobData !== null) {
            setHeader('Edit Files')
            setEditId(props.popupInfo.jobData.id)
            if (props.popupInfo.jobData.fileDescription) {
                setDescription(props.popupInfo.jobData.fileDescription)
            }
            if (props.popupInfo.jobData.projectId) {
                setProjectId(props.popupInfo.jobData.projectId)              
            }

            if (Array.isArray(JSON.parse(props.popupInfo.jobData.jobFileData))) {
                let jobData = JSON.parse(props.popupInfo.jobData.jobFileData)
                let result = []
                for (let jobDataItr = 0; jobDataItr < jobData.length; jobDataItr++) {
                    result.push(jobData[jobDataItr])
                    setFilesPresaved(result)
                }
            } else {
                let jobData = JSON.parse(props.popupInfo.jobData.jobFileData)
                if (jobData && jobData.name !== undefined) {
                    setFilesPresaved([jobData])
                }
            }
        }
    }, [projectId])

    function handleClick(event) {
        event.preventDefault()

        if (event.target.className !== 'MuiDialog-container MuiDialog-scrollPaper css-hz1bth-MuiDialog-container' &&
            event.target.className !== "MuiDialog-container MuiDialog-scrollPaper css-ekeie0") {
            let allCurrentlyUploadedBlobs = []
            for (let x = 0; x < uploadedFiles.length; x++) {
                allCurrentlyUploadedBlobs.push(uploadedFiles[x]["blobName"])
            }
            props.dataToolsDeleteBlobsFromUnfinishedJob(allCurrentlyUploadedBlobs)
            props.closePopup()
        }
    }

    function handleJobStepsSave(event) {
        event.preventDefault()
          
        if (editId === -1 && projectId === -1) {
            setErrorMessage('A project must be selected before uploading.')
            setInvalid(true)
            return
        }

        let files = filesPresaved

        if (files.length === 0) {
            files = uploadedFiles
        }

        if (files.length > 0) {
            setIsLoadingRequest(true)

            if (editId > -1 && uploadedNewFiles) {
                props.purgePreviosJobFiles(editId).then((resp) => {
                    if (resp.status !== 200) {
                        setIsLoadingRequest(false)
                        setErrorMessage('Error when purging older files')
                        setInvalid(true)
                    } else {
                        props.saveDatabaseJobFile(editId, uploadedFiles, description, fileSnippets, projectId).then((resp) => {
                            setIsLoadingRequest(false)
                            props.getDatabaseJobFiles()
                            props.closePopup()
                        })
                    }
                })
            } else {
                props.saveDatabaseJobFile(editId, files, description, fileSnippets, projectId).then((resp) => {
                    setIsLoadingRequest(false)
                    props.getDatabaseJobFiles()
                    props.closePopup()
                })
            }
        }
    }

    function renderSubmitButton() {
        if (isLoadingRequest) {
            return <button type="submit" className="btn" disabled onClick={() => { }}>Saving...</button>
        }
        if (invalid) {
            return <button type="submit" className="btn" disabled onClick={() => { }}>Invalid File</button>
        }
        if (fileSizeProcessed > 0) {
            return <button type="submit" className="btn" onClick={handleJobStepsSave}>OK</button>
        }
        return <button type="submit" className="btn" onClick={handleJobStepsSave}>Upload File</button>
    }

    function renderFileNameLine() {
        if (uploadedFiles.length === 0) {
            if (filesPresaved.length === 0) {
                if (isLoadingRequest) {
                    return <p>Uploading...</p>
                } else {
                    return <p>Drag 'n' drop some files here, or click to select files</p>
                }
            } else {
                return <>
                    {filesPresaved.map((file, fIndex) => <p key={`file-${fIndex}`}>{file.name}</p>)}
                </>
            }
        } else {
            return <>
                {uploadedFiles.map(file => <p key={`file-${file.lastModified}`}>{file.name}</p>)}
            </>
        }
    }

    function renderProgressBar() {
        if (isLoadingRequest && combinedUploadFileSizes > 0) {
            return (<div className="progressBar" style={{ "width": "100%" }}><div className="filler" style={{ width: `${(fileSizeProcessed / combinedUploadFileSizes) * 100}%` }}></div></div>)
        }
        return null
    }

    function isThisFileTypeAccepted(fileName) {
        let fileExtension = getFileExtension(fileName)
        switch (fileExtension) {
            case 'txt':
            case 'json':
            case 'xls':
            case 'xlsx':
            case 'xdb':
            case 'accdb':
            case 'mdb':
            case 'csv':
            case 'xmp':
            case 'xpi':
                return true
            default: return false
        }
    }

    function isThisFileZipped(fileName) {
        return getFileExtension(fileName) === 'zip'
    }

    function removeItemOnce(arr, value) {
        var index = arr.indexOf(value);
        if (index > -1) {
            arr.splice(index, 1);
        }
        return arr;
    }

    function generateGUID() {
        return 'xxxxxxxx-xxxx-xxxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0,
                v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    function lookupIfWeNeedNewGuid(jobFileData, name, newFileMode) {
        if (jobFileData === null) {
            return generateGUID()
        } else if (newFileMode) {
            return generateGUID()
        }

        let jobData = JSON.parse(jobFileData)
        if (jobData && jobData.identifier !== undefined) {
            return jobData.identifier
        }

        return generateGUID()
    }

    function getFileExtension(fileName) {
        return fileName.slice((fileName.lastIndexOf(".") - 1 >>> 0) + 2);
    }

    function areExtensionsEqual(jobFileData, fileName1, fileName2) {
        if (jobFileData === null) {
            return
        }
        return getFileExtension(fileName1) === getFileExtension(fileName2);
    }

    function doesThisFileNameExistsWithin(existingFiles, allFileNames) {
        let rtnVal = []
        for (let e = 0; e < existingFiles.length; e++) {
            let jobFileData = JSON.parse(existingFiles[e]["jobFileData"])
            if (jobFileData && jobFileData["zipIndividualFiles"] && jobFileData["zipIndividualFiles"].length > 0) {
                let zipIndividualFiles = jobFileData["zipIndividualFiles"]
                let lookup = zipIndividualFiles.filter(z => allFileNames.includes(z.toLowerCase()))
                for (let x = 0; x < lookup.length; x++) {
                    rtnVal.push(lookup[x])
                }
            }
        }
        return rtnVal
    }

    function hasValidExtension(filename) {
        return /\.(csv|txt)$/i.test(filename)
    }

    async function doesThisZipFileContainOneFileThatWasUploadedIndividually(uploadedFiles, existingFiles) {
        let rtnVal = []
        let allFileNames = []
        let allFileNamesWithinZip = []

        for (let fileItr = 0; fileItr < existingFiles.length; fileItr++) {
            if (existingFiles[fileItr]["fileName"]) {
                allFileNames.push(existingFiles[fileItr]["fileName"].toLowerCase().trim())
            } else if (existingFiles[fileItr]["name"]) {
                allFileNames.push(existingFiles[fileItr]["name"].toLowerCase().trim())
            }
        }

        for (let uploadedFileItr = 0; uploadedFileItr < uploadedFiles.length; uploadedFileItr++) {
            let isZip = false
            if (isThisFileZipped(uploadedFiles[uploadedFileItr]["name"])) {
                isZip = true
            }

            let fileNames = []
            if (isZip) {
                let zipCall = await JSZip.loadAsync(uploadedFiles[uploadedFileItr])
                fileNames = Object.keys(zipCall["files"])
                for (let x = 0; x < fileNames.length; x++) {
                    allFileNamesWithinZip.push(fileNames[x].toLowerCase().trim())
                }
            }
        }

        let lookup = allFileNamesWithinZip.filter(z => allFileNames.includes(z.toLowerCase()))
        for (let x = 0; x < lookup.length; x++) {
            rtnVal.push(lookup[x])
        }

        return rtnVal
    }

    return <Dialog
        maxWidth={"lg"}
        fullWidth={true}
        open={true}
        fullScreen={false}
        onClose={handleClick}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
    >
        <CloseIcon onClick={handleClick} className="closePoup" />
        <DialogTitle id="alert-dialog-title" className="changepassdialog">
            {header}
            {' '}
        </DialogTitle>
        <DialogContent>
            <form className="form dbFunctions">
                {editId === -1 && (
                    <div className="field">
                        <FormLabel component="legend">Select Project</FormLabel>
                        <Select
                            className="myInput"
                            value={projectId}
                            onChange={(e) => {
                                setProjectId(parseInt(e.target.value))
                                setErrorMessage('')
                                setInvalid(false)
                            }}
                            inputProps={{ 'aria-label': 'Without label' }}
                            displayEmpty
                        >
                            {projectList.map((x) => (
                                <MenuItem key={x.id} value={x.id}>{x.projectName}</MenuItem>
                            ))}
                        </Select>
                    </div>
                )}
                <div {...getRootProps()} className="field">
                    <input {...getInputProps()} />
                    {isDragActive ? <p>Drop the files here ...</p> : renderFileNameLine()}
                </div>
                <div className="field">
                    <TextField
                        fullWidth
                        label="File Description"
                        variant="outlined"
                        value={description}
                        disabled={false}
                        onChange={(e) => { e.preventDefault(); setDescription(e.target.value); }} />
                </div>
                <div className="field" style={{ color: '#d32f2f' }}>{errorMessage}</div>
                <div className="field">
                    {renderProgressBar()}
                </div>
                <div className="field">
                    <button style={{ "marginLeft": "5px" }} className="btn" onClick={handleClick}>Cancel</button>&nbsp;&nbsp;
                    {renderSubmitButton()}
                </div>
            </form>
        </DialogContent>
    </Dialog>
}

function mapStateToProps(state) {
    return {
        popupInfo: state.ui.popupInfo,
        role: state.users.role,
        currentUser: state.users.currentUser
    }
}

function mapDispatchToProps(dispatch) {
    return {
        ...bindActionCreators({ closePopup, uploadFileData, getDatabaseJobFiles, saveDatabaseJobFile, deleteDatabaseJobFile, purgePreviosJobFiles, dataToolsDeleteBlobsFromUnfinishedJob }, dispatch)
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(DatabaseJobFileUpload)